<template>
    <div>
        <vue-simple-header>
            <span slot="headerTitle">
                Wiki Exports
            </span>
            <span slot="panelRight">
                <vue-folder-button
                        @onClick="createReport"
                        color="blue"
                        text="Create Export"
                        icon-class="fa-plus"
                        aria-label="Create Export"
                ></vue-folder-button>
                <vue-folder-button
                        :target="backUrl"
                        color="blue"
                        text="Back"
                        icon-class="fa-reply"
                        aria-label="Back"
                ></vue-folder-button>
            </span>
        </vue-simple-header>
        <div class="row margin-15-top">
            <div class="col-sm-12">
                <div class="users_table_outer_container">
                    <table class="table table-striped">
                        <thead>
                        <tr>
                            <th>#ID</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Created at</th>
                            <th>Link</th>
                            <th>Delete</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="report in reports">
                            <td>#{{ report.id }}</td>
                            <td>{{ report.name }}</td>
                            <td>{{ getStatusRepresentation(report) }}</td>
                            <td>{{ report.completed_time }}</td>
                            <td>
                                <a :href="report.link" v-if="report.link">Download</a>
                                <span v-else> - </span>
                            </td>
                            <td class="text-center">
                                <i class="fas fa-trash pointer color-footer-red"
                                   tabindex="0"
                                   style="font-size: 18px;"
                                   role="button"
                                   aria-label="Delete Report"
                                   v-tooltip="'Delete'"
                                   @click="deleteReport(report)"
                                   @keydown.enter="deleteReport(report)"
                                ></i>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <div class="alert alert-info" v-if="!loading&&reports.length===0">
                    Nothing to show
                </div>
            </div>
        </div>
        <div class="row" v-if="totalPage>1">
            <div class="col-sm-12 text-right">
                <pagination v-model="currentPage" :total-page="totalPage" @change="changePage"/>
            </div>
        </div>

    </div>
</template>

<script>

    import BackButtonMixin from '~/vue/mixins/BackButtonMixin';
    import moment from 'moment';
    import WikiReportsResource from '~/angular/app/wiki_page/reports/wiki_reports_resource';
    import WikiMembershipResource from '~/angular/app/wiki/wiki_settings_page/wiki_membership_resource';
    import TreeSelect from '@riophae/vue-treeselect';
    import '@riophae/vue-treeselect/dist/vue-treeselect.css';
    import { simplePopupFactory } from '~/angular/app/shared/simple_popups/simplePopupFactory';
    import Vue from 'vue';
    import CreateWikiReportPupup from '~/angular/app/wiki_page/reports/CreateWikiReportPupup.vue';

    const STATUSES_VISUAL = {
        3: 'Created',
        0: 'Queued',
        1: 'Processing',
        2: 'Completed',
        4: 'Error',
        5: 'No results',
    };

    const TIME_ZONE = 'Europe/London';
    const TODAY = moment.tz(TIME_ZONE).toDate();
    const VISUAL_DATE_FORMAT = 'DD/MM/YYYY';
    const REPORT_NAME_DATE_FORMAT = 'YYYYMMDD';

    const RESULTS_PER_PAGE = 10;

    export default {
        name: 'WikiPageReports',
        components: { TreeSelect },
        mixins: [BackButtonMixin],
        props: {
            wikiId: [Number, String],
        },
        data() {
            return {
                selectedItems: [],
                rawWikiSections: [],
                reports: [],
                loading: false,
                currentPage: 1,
                totalPage: 1,

                updatingReports: false,
                wikiTree: {},
            };
        },
        computed: {
            sectionsToSelect() {
                function parseSection(s) {
                    return {
                        id: 's_' + s.id,
                        label: s.caption,
                        children: (s.subsections?.length) ? s.subsections.map(ss => parseSection(ss)) : (s.slides?.length) ? s.slides.map(ss => parseSlide(ss)) : undefined,
                    };
                }

                function parseSlide(s) {
                    return {
                        id: 'sl_' + s.id,
                        label: `[${s.caption || `Slide #${s.id}`}]`,
                        children: undefined,
                    };
                }

                return [
                    {
                        id: 'wiki',
                        label: 'Wiki',
                        children: this.rawWikiSections.map(s => parseSection(s))
                    },
                ];
            },
            $_reportsIdsToCheckStatus() {
                return this.reports.filter(r => !r.ready).map(r => r.id);
            },
        },
        mounted() {
            this.fetchReports();
            this.getWikiTree();
            this.$_enableStatusUpdater();

        },
        beforeDestroy() {
            this.$_disableStatusUpdater();
        },
        methods: {
            $_updateReportsStatus() {
                if (this.updatingReports) {
                    return;
                }
                if (this.$_reportsIdsToCheckStatus.length) {
                    this.updatingReports = true;
                    WikiReportsResource.update_reports_status(this.wikiId, this.$_reportsIdsToCheckStatus)
                        .then(resp => {
                            for (const report of resp.data) {
                                const reportToUpdate = this.reports.filter(r => r.id === report.id)[0];
                                if (reportToUpdate) {
                                    Vue.set(this.reports, this.reports.indexOf(reportToUpdate), report);
                                }
                            }
                        }, err => {
                        })
                        .finally(() => {
                            this.updatingReports = false;
                        });
                }
            },
            $_enableStatusUpdater() {
                this.updaterInterval = setInterval(this.$_updateReportsStatus, 2500);
            },
            $_disableStatusUpdater() {
                if (this.updaterInterval !== undefined) {
                    clearInterval(this.updaterInterval);
                }
            },
            getStatusRepresentation(report) {
                return STATUSES_VISUAL[report.status];
            },
            $_suggestName() {
                let name = `Export `;
                const wiki_included = this.selectedItems.includes('wiki');
                const sections_ids = this.selectedItems.filter(sl => sl.startsWith('s_'));
                const slides_ids = this.selectedItems.filter(sl => sl.startsWith('sl_'));
                name += ` ${moment(TODAY).format(REPORT_NAME_DATE_FORMAT)} `;
                if (wiki_included) {
                    name += 'Whole Wiki';
                } else {
                    if (sections_ids.length) {
                        name += `${sections_ids.length} Sections`;
                    } else if (slides_ids.length) {
                        name += `${slides_ids.length} Pages`;
                    }
                }
                return name.slice(0, 60);
            },
            async fetchReports() {
                this.$loadingSpinner.show('reports');
                try {
                    const { data } = await WikiReportsResource.get_reports(this.wikiId, {
                        page: this.currentPage,
                        size: RESULTS_PER_PAGE
                    });
                    this.reports = data.results;
                    this.totalPage = Math.ceil(data.count / RESULTS_PER_PAGE);
                } catch (e) {
                    this.$notifications.error(e);
                }
                this.$loadingSpinner.hide('reports');
                this.$loadingSpinner.hide();
            },
            async changePage() {
                await this.fetchReports();
            },
            async getWikiTree() {
                const { data } = await WikiMembershipResource().get_wiki_tree(this.wikiId);
                this.rawWikiSections = data.sections;
            },
            async deleteReport(report) {
                const confirmed = await simplePopupFactory.show_popup('Delete Export', 'Are you sure you want to delete this export?', 'Delete', 'Cancel', 'btn-red').catch(_.noop);
                if (!confirmed) {
                    return;
                }
                this.$loadingSpinner.show();
                let restReportsCount;
                try {
                    const { data } = await WikiReportsResource.delete_report(this.wikiId, report.id);
                    restReportsCount = data.total_count;
                } catch (e) {
                    this.$notifications.error(e);
                    this.$loadingSpinner.hide();
                    return;
                }
                const reportIndex = this.reports.indexOf(report);
                this.reports.splice(reportIndex, 1);
                const totalPage = Math.ceil(restReportsCount / RESULTS_PER_PAGE);
                if (this.currentPage > totalPage) {
                    this.currentPage = totalPage;
                } else if (this.currentPage < totalPage) {
                    try {
                        await this.fetchReports();
                    } catch (e) {
                    }
                }
                this.$loadingSpinner.hide();
            },
            async createReport() {
                const {
                    popupComponent,
                    fEl,
                    vm
                } = simplePopupFactory.mount_vue_popup(CreateWikiReportPupup);
                const res = await popupComponent.show(this.sectionsToSelect).catch(() => {
                });
                vm?.$destroy();
                fEl?.focus();
                if (!res) {
                    return;
                }
                const {selectedItems, reportName, includeFullLog} = res;
                const wiki_included = selectedItems.includes('wiki');
                const sections_ids = selectedItems.filter(sl => sl.startsWith('s_'));
                const slides_ids = selectedItems.filter(sl => sl.startsWith('sl_'));
                this.$loadingSpinner.show();
                try {
                    const { data } = await WikiReportsResource.create_report(this.wikiId, {
                        name: reportName,
                        include_wiki: wiki_included,
                        include_sections: sections_ids.map(i => parseInt(i.substring(2))),
                        include_slides: slides_ids.map(i => parseInt(i.substring(3))),
                        include_full_log: includeFullLog,
                    });
                    this.reports.unshift(data.report);
                    this.totalPage = Math.ceil(data.total_count / RESULTS_PER_PAGE);

                    if ((this.reports.length > RESULTS_PER_PAGE) && (this.currentPage === 1)) {
                        this.reports.pop();
                    }
                    this.$notifications.success('Created');
                } catch (e) {
                    console.log(e);
                    this.$notifications.error(e);
                }
                this.$loadingSpinner.hide();
            },
        },

    };
</script>

<style scoped lang="scss">


</style>
